// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';
import { normalizarBusca } from '@/nucleo/utilitarios';

import type { Cooperado, FiltroDeCooperados } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados de "Meus cooperados".
 *
 * Mesmo desenho de `condutores/dados.ts`: nenhum componente busca dado
 * direto, e filtro/paginação são resolvidos aqui mesmo sobre o array fixo de
 * `dados.mock.json` — ver a justificativa em `../comum/mock.ts`.
 */
export const CHAVES_DE_COOPERADOS = {
  lista: (filtro: FiltroDeCooperados) => ['credenciado', 'cooperados', 'lista', filtro] as const,
};

const TODOS_OS_COOPERADOS = dadosMock as Cooperado[];

function aplicarFiltro(itens: Cooperado[], filtro: FiltroDeCooperados): Cooperado[] {
  return itens.filter((cooperado) => {
    if (filtro.situacao && cooperado.situacao !== filtro.situacao) {
      return false;
    }

    if (filtro.busca) {
      const alvo = normalizarBusca(filtro.busca);
      const documentoBuscado = filtro.busca.replace(/\D/g, '');
      const combinaPorNome = normalizarBusca(cooperado.nome).includes(alvo);
      const combinaPorDocumento =
        documentoBuscado.length > 0 && cooperado.cpf_cnpj.includes(documentoBuscado);
      if (!combinaPorNome && !combinaPorDocumento) {
        return false;
      }
    }

    return true;
  });
}

export function useCooperados(
  filtro: FiltroDeCooperados,
): UseQueryResult<Pagina<Cooperado>, Error> {
  return useQuery<Pagina<Cooperado>, Error>({
    queryKey: CHAVES_DE_COOPERADOS.lista(filtro),
    queryFn: () =>
      resolverMock(
        paginar(aplicarFiltro(TODOS_OS_COOPERADOS, filtro), filtro.pagina, filtro.tamanho),
      ),
    placeholderData: (anterior) => anterior,
  });
}
